import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { useDarkMode } from "@rbnd/react-dark-mode";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { teacher } from "../puglins/Utils/models";
import notistack from "../puglins/notistack";
import { consumeMissedRequest, consumeOfflineByTeacher, goOnline, missingItemsOf, onCourseAccepted, onOnlinePrompt } from "../helper/teacherPresence";
import { PlanningModalStyle, PlanningUi } from "./styles/planningUi";

// 🎨 Charte du site : pictogramme d'état, texte court, boutons pleine largeur sur téléphone ;
// ce qui manque, une ligne par point avec son icône et le lien pour le régler
const MISSING_ICONS = { phone: "solar:phone-linear", iban: "solar:card-linear", stripe: "solar:shield-user-linear" };

// Tableau de bord du prof (boutons En ligne et Hors ligne) : seule page où la fenêtre s'ouvre
// d'elle-même, une fois par session (onglet)
const DASHBOARD = "/dashboard";
// Salle de cours : jamais de « Passer en ligne ? » pendant un cours (tour 65, A11)
const ROOM = "/zoomclasses";
const SHOWN_KEY = "teacherOnlinePromptShown";
const shownThisSession = () => {
  try {
    return sessionStorage.getItem(SHOWN_KEY) === "1";
  } catch (err) {
    return false;
  }
};
const markShown = () => {
  try {
    sessionStorage.setItem(SHOWN_KEY, "1");
  } catch (err) {}
};

// 🟢 Prof hors ligne après une connexion ou une reconnexion : « Vous êtes hors ligne. Passer en
// ligne ? », à l'arrivée sur son tableau de bord, une fois par session ; jamais sur les pages
// publiques (accueil, désinscription, CGU…). Le passage en ligne est vérifié par l'API ; s'il
// manque quelque chose (téléphone, IBAN, compte Stripe), la liste s'affiche avec les liens pour
// le régler (aussi après un clic sur « En ligne », depuis n'importe quelle page du compte).
const TeacherOnlinePrompt = ({ authUserData, onChanged }) => {
  const { t } = useTranslation();
  const { mode } = useDarkMode();
  const { pathname } = useLocation();
  const onDashboard = pathname.toLowerCase() === DASHBOARD;
  const inRoom = pathname.toLowerCase().replace(/\/+$/, "") === ROOM;
  const [open, setOpen] = useState(false);
  const [wanted, setWanted] = useState(false); // demandée (connexion, page ouverte) : attend le tableau de bord
  const [missing, setMissing] = useState(null);
  // Hors ligne après une demande d'élève restée sans réponse (tour 62, B4) : la fenêtre dit pourquoi
  const [missedRequest, setMissedRequest] = useState(false);
  const [busy, setBusy] = useState(false);
  const lastOnline = useRef(authUserData?.online);

  useEffect(
    () =>
      onOnlinePrompt((detail) => {
        if (detail?.missing) {
          // « En ligne » refusé : ce qui manque, tout de suite
          setMissing(detail.missing);
          setOpen(true);
        } else setWanted(true);
      }),
    []
  );

  // Demande d'élève acceptée : la fenêtre se ferme, le prof part en cours (tour 65, A11)
  useEffect(
    () =>
      onCourseAccepted(() => {
        setOpen(false);
        setWanted(false);
        setMissing(null);
        setMissedRequest(false);
      }),
    []
  );

  // Arrivée sur le tableau de bord, hors ligne : la fenêtre, une seule fois par session
  useEffect(() => {
    if (!wanted || !onDashboard || authUserData?.online !== 0) return;
    setWanted(false);
    if (shownThisSession()) return;
    markShown();
    setMissing(null);
    setMissedRequest(false);
    setOpen(true);
  }, [wanted, onDashboard, authUserData?.online]);

  // Mis hors ligne sans l'avoir demandé (absence de plus de 4 minutes, coupure, autre onglet, demande
  // d'élève restée sans réponse) pendant qu'il est sur son tableau de bord : on lui propose de repasser en ligne.
  // Après une demande restée sans réponse (tour 63, A1) : sur la page où il se trouve, une fois par demande manquée
  useEffect(() => {
    const before = lastOnline.current;
    lastOnline.current = authUserData?.online;
    if (before !== 1 || authUserData?.online !== 0) return;
    const missed = consumeMissedRequest();
    if (!consumeOfflineByTeacher() && (onDashboard || missed)) {
      setMissing(null);
      setMissedRequest(missed);
      setOpen(true);
    }
  }, [authUserData?.online]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authUserData?.userType !== 1 || !open || inRoom || authUserData?.online === 1 || authUserData?.online === 2) return null;

  const close = () => {
    setOpen(false);
    setMissing(null);
    setMissedRequest(false);
  };
  const tryOnline = async () => {
    setBusy(true);
    try {
      const result = await goOnline(teacher, authUserData._id);
      if (result.cancelled) return;
      if (result.missing) setMissing(result.missing);
      else {
        notistack.success(t("teacherOnline.nowOnline"));
        close();
        onChanged?.();
      }
    } catch (err) {
      notistack.error(err?.response?.data?.error || t("notifications.status_update_error"));
    } finally {
      setBusy(false);
    }
  };
  const openStripe = async () => {
    setBusy(true);
    try {
      const { url } = await teacher.stripeAccountLink();
      window.location.assign(url);
    } catch (err) {
      notistack.error(err?.response?.data?.error || t("teacherOnline.stripeLinkError"));
      setBusy(false);
    }
  };

  return (
    <>
      <PlanningModalStyle />
      <Modal show onHide={close} centered className="acd-modal">
        <Modal.Body data-testid="teacher-online-prompt">
          <PlanningUi mode={mode} className="p-4">
            {!missing ? (
              <>
                <div className="text-center mb-4">
                  <span className="acd-chip acd-chip-orange mb-3">
                    <Icon icon="solar:power-linear" width={16} aria-hidden="true" />
                    {t("offline")}
                  </span>
                  <h5 className="acd-card-title justify-content-center mb-2">{t("teacherOnline.offline")}</h5>
                  <p className="acd-muted mb-0">{t(missedRequest ? "teacherOnline.missedHelp" : "teacherOnline.offlineHelp")}</p>
                </div>
                <div className="acd-actions">
                  <button type="button" className="acd-btn acd-btn-ghost" disabled={busy} onClick={close}>{t("teacherOnline.stayOffline")}</button>
                  <button type="button" className="acd-btn acd-btn-primary" disabled={busy} onClick={tryOnline}>{t("teacherOnline.goOnline")}</button>
                </div>
              </>
            ) : (
              <>
                <h5 className="acd-card-title mb-2">
                  <Icon icon="solar:danger-triangle-linear" width={22} className="acd-ico" aria-hidden="true" />
                  {t("teacherOnline.missingTitle")}
                </h5>
                <ul className="acd-recap mb-3" data-testid="teacher-online-missing">
                  {missingItemsOf(missing).map((item) => (
                    <li key={item.key}>
                      <Icon icon={MISSING_ICONS[item.key] || "solar:info-circle-linear"} width={20} className="acd-ico" aria-hidden="true" />
                      <div>
                        {t(item.text)}{" "}
                        {item.link && (
                          <Link to={item.link} className="fw-semibold" onClick={close}>{t(item.linkText)}</Link>
                        )}
                        {item.stripeLink && (
                          <Button variant="link" className="p-0 align-baseline fw-semibold" disabled={busy} onClick={openStripe}>{t("teacherOnline.links.stripe")}</Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="acd-actions">
                  <button type="button" className="acd-btn acd-btn-ghost" disabled={busy} onClick={close}>{t("teacherOnline.close")}</button>
                  <button type="button" className="acd-btn acd-btn-primary" disabled={busy} onClick={tryOnline}>{t("teacherOnline.retry")}</button>
                </div>
              </>
            )}
          </PlanningUi>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TeacherOnlinePrompt;
